import type { CustomerProfileApiInterface } from '@/api/customerProfile/types/index.ts';

let cachedApi: CustomerProfileApiInterface | null = null;

/**
 * Resolve the customer-profile API, returning a mock when running under
 * Storybook or with the dev mock flag set.
 *
 * Vue CLI replaces `process.env.VUE_APP_*` at build time, so webpack
 * dead-code-eliminates the mock branch in production builds. When the mock is
 * used outside Storybook, this also seeds the stores its fixtures presuppose,
 * so consumers never need to know the mock is active.
 */
export async function getApi({
    delayIfMocked = 3000,
}: { delayIfMocked?: number } = {}): Promise<CustomerProfileApiInterface> {
    if (cachedApi) return cachedApi;

    // @ts-expect-error import.meta.env.STORYBOOK is injected by Storybook
    const isStorybook = import.meta.env.STORYBOOK === 'true';
    const useMock = process.env.VUE_APP_USE_MOCK_CUSTOMER_API === 'true' || isStorybook;

    if (useMock) {
        const { default: CustomerProfileAPIMock } = await import(
            '@/api/customerProfile/__mocks__/index.mock.ts'
        );
        cachedApi = new CustomerProfileAPIMock({ delay: isStorybook ? 300 : delayIfMocked });
        // Seed the stores the mock fixtures presuppose at the mock seam, so
        // consumers never need to know the mock is active. Storybook seeds its
        // own account via a decorator, so this only covers the dev env-flag path.
        if (!isStorybook) {
            const { seedMockAccount } = await import(
                '@/api/customerProfile/__mocks__/seedMockAccount.ts'
            );
            seedMockAccount();
        }
    } else {
        const { default: CustomerProfileAPI } = await import('@/api/customerProfile/index.ts');
        cachedApi = new CustomerProfileAPI();
    }

    return cachedApi;
}
